import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  resource,
  ResourceRef,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  CONFIGURATION_SETTING_KEYS,
  deriveTicketIsActive,
  ISortGroupWithTicketData,
  ITicket,
  TicketCategoryId,
  TicketId,
} from '@web/dtos';
import { SortGroupsTicketDataGateway } from './sort-groups-ticket-data.gateway';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { ConfigurationStore } from '../configuration/configuration-store.service';
import { TicketGateway } from '../tickets/ticket.gateway';

@Injectable({ providedIn: 'root' })
export class SortGroupsWithTicketDataStore {
  private readonly gateway: SortGroupsTicketDataGateway =
    new SortGroupsTicketDataGateway();
  private readonly ticketGateway: TicketGateway = new TicketGateway();
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);

  #sortGroupsResource: ResourceRef<
    IApiResponse<ISortGroupWithTicketData[]> | undefined
  > = resource({
    params: computed(() =>
      this.configurationStore.getNumberConfiguration(
        CONFIGURATION_SETTING_KEYS.ServiceAreaId
      )
    ),
    loader: ({ params }) => firstValueFrom(this.gateway.list(params)),
  });

  public readonly isLoading: Signal<boolean> = computed(() =>
    this.#sortGroupsResource.isLoading()
  );
  private readonly loadedSortGroups: Signal<ISortGroupWithTicketData[]> =
    computed(() =>
      this.#sortGroupsResource.hasValue()
        ? (this.#sortGroupsResource.value()?.data ?? [])
        : []
    );
  private readonly writableSortGroups: WritableSignal<
    ISortGroupWithTicketData[]
  > = linkedSignal(() => this.loadedSortGroups());

  public readonly sortGroups: Signal<ISortGroupWithTicketData[]> =
    this.writableSortGroups;
  public readonly ticketData: Signal<ITicket[]> = computed(() =>
    this.writableSortGroups().flatMap(
      (sortGroup: ISortGroupWithTicketData): ITicket[] =>
        sortGroup.ticketData ? [sortGroup.ticketData] : []
    )
  );

  public assignTicketToCategory(
    ticketGroup: ISortGroupWithTicketData,
    ticketCategoryId: string,
    position?: number
  ): void {
    const sortGroups: ISortGroupWithTicketData[] = this.writableSortGroups();
    const movedSortGroupIndex: number = sortGroups.indexOf(ticketGroup);

    if (movedSortGroupIndex < 0) {
      return;
    }

    const movedSortGroup: ISortGroupWithTicketData =
      sortGroups[movedSortGroupIndex];
    const previousCategoryId: string | null =
      movedSortGroup.ticketData?.ticketCategoryId ?? null;
    const targetPosition: number =
      position ??
      sortGroups.filter(
        (
          sortGroup: ISortGroupWithTicketData,
          sortGroupIndex: number
        ): boolean =>
          sortGroup.ticketData?.ticketCategoryId === ticketCategoryId &&
          sortGroupIndex !== movedSortGroupIndex
      ).length;

    let updatedSortGroups: ISortGroupWithTicketData[] = sortGroups.map(
      (
        sortGroup: ISortGroupWithTicketData,
        sortGroupIndex: number
      ): ISortGroupWithTicketData => {
        if (sortGroupIndex !== movedSortGroupIndex) {
          return sortGroup;
        }

        return {
          ...sortGroup,
          ticketData: {
            ...(sortGroup.ticketData ?? this.createTicketData(sortGroup)),
            ticketCategoryId: ticketCategoryId as TicketCategoryId,
            positionInCategory: targetPosition,
            isActive: deriveTicketIsActive(
              ticketCategoryId as TicketCategoryId
            ),
          },
        };
      }
    );

    if (previousCategoryId && previousCategoryId !== ticketCategoryId) {
      updatedSortGroups = this.normalizeCategoryPositions(
        updatedSortGroups,
        previousCategoryId
      );
    }

    updatedSortGroups = this.normalizeCategoryPositions(
      updatedSortGroups,
      ticketCategoryId,
      movedSortGroupIndex,
      targetPosition
    );
    this.writableSortGroups.set(updatedSortGroups);
  }

  public assignTicketsToCategory(
    ticketGroups: ISortGroupWithTicketData[],
    ticketCategoryId: string
  ): void {
    ticketGroups.forEach((ticketGroup: ISortGroupWithTicketData): void =>
      this.assignTicketToCategory(ticketGroup, ticketCategoryId)
    );
  }

  public removeTicketFromCategory(ticketGroup: ISortGroupWithTicketData): void {
    const sortGroups: ISortGroupWithTicketData[] = this.writableSortGroups();
    const movedSortGroupIndex: number = sortGroups.indexOf(ticketGroup);
    const movedSortGroup: ISortGroupWithTicketData | undefined =
      sortGroups[movedSortGroupIndex];
    const previousCategoryId: string | null =
      movedSortGroup?.ticketData?.ticketCategoryId ?? null;

    if (!movedSortGroup?.ticketData || !previousCategoryId) {
      return;
    }

    const updatedSortGroups: ISortGroupWithTicketData[] =
      this.writableSortGroups().map(
        (
          sortGroup: ISortGroupWithTicketData,
          sortGroupIndex: number
        ): ISortGroupWithTicketData => {
          if (sortGroupIndex !== movedSortGroupIndex || !sortGroup.ticketData) {
            return sortGroup;
          }

          return {
            ...sortGroup,
            ticketData: {
              ...sortGroup.ticketData,
              ticketCategoryId: null,
              positionInCategory: null,
              isActive: deriveTicketIsActive(null),
            },
          };
        }
      );

    this.writableSortGroups.set(
      this.normalizeCategoryPositions(updatedSortGroups, previousCategoryId)
    );
  }

  public moveTicketInCategory(
    ticketGroup: ISortGroupWithTicketData,
    direction: number
  ): void {
    const sortGroupIndex: number =
      this.writableSortGroups().indexOf(ticketGroup);
    const sortGroup: ISortGroupWithTicketData | undefined =
      this.writableSortGroups()[sortGroupIndex];
    const ticketCategoryId: string | null =
      sortGroup?.ticketData?.ticketCategoryId ?? null;
    const currentPosition: number | null =
      sortGroup?.ticketData?.positionInCategory ?? null;

    if (!sortGroup || !ticketCategoryId || currentPosition === null) {
      return;
    }

    const numberOfTicketsInCategory: number = this.writableSortGroups().filter(
      (currentSortGroup: ISortGroupWithTicketData): boolean =>
        currentSortGroup.ticketData?.ticketCategoryId === ticketCategoryId
    ).length;
    const targetPosition: number = currentPosition + direction;

    if (targetPosition < 0 || targetPosition >= numberOfTicketsInCategory) {
      return;
    }

    this.assignTicketToCategory(sortGroup, ticketCategoryId, targetPosition);
  }

  public removeTicketCategory(ticketCategoryId: string): void {
    this.writableSortGroups.update(
      (sortGroups: ISortGroupWithTicketData[]): ISortGroupWithTicketData[] =>
        sortGroups.map(
          (sortGroup: ISortGroupWithTicketData): ISortGroupWithTicketData => {
            if (sortGroup.ticketData?.ticketCategoryId !== ticketCategoryId) {
              return sortGroup;
            }

            return {
              ...sortGroup,
              ticketData: {
                ...sortGroup.ticketData,
                ticketCategoryId: null,
                positionInCategory: null,
                isActive: deriveTicketIsActive(null),
              },
            };
          }
        )
    );
  }

  public reset(): void {
    this.writableSortGroups.set(
      this.loadedSortGroups().map(
        (sortGroup: ISortGroupWithTicketData): ISortGroupWithTicketData => ({
          ...sortGroup,
          ...(sortGroup.ticketData
            ? { ticketData: { ...sortGroup.ticketData } }
            : {}),
        })
      )
    );
  }

  public async saveTicketData(): Promise<IApiResponse<ITicket[]>> {
    return firstValueFrom(this.ticketGateway.saveAll(this.ticketData()));
  }

  public reloadSortGroups(): void {
    this.#sortGroupsResource.reload();
  }

  private createTicketData(sortGroup: ISortGroupWithTicketData): ITicket {
    return {
      id: crypto.randomUUID() as TicketId,
      ticketId: sortGroup.externeReferenz ?? sortGroup.name,
      serviceAreaId: this.configurationStore.getNumberConfiguration(
        CONFIGURATION_SETTING_KEYS.ServiceAreaId
      ),
      isActive: false,
      ticketCategoryId: null,
      positionInCategory: null,
    };
  }

  private normalizeCategoryPositions(
    sortGroups: ISortGroupWithTicketData[],
    ticketCategoryId: string,
    movedSortGroupIndex?: number,
    targetPosition?: number
  ): ISortGroupWithTicketData[] {
    const categorySortGroupIndexes: number[] = sortGroups
      .map(
        (
          sortGroup: ISortGroupWithTicketData,
          sortGroupIndex: number
        ): number | undefined =>
          sortGroup.ticketData?.ticketCategoryId === ticketCategoryId
            ? sortGroupIndex
            : undefined
      )
      .filter(
        (sortGroupIndex: number | undefined): sortGroupIndex is number =>
          sortGroupIndex !== undefined
      )
      .sort(
        (firstSortGroupIndex: number, secondSortGroupIndex: number): number =>
          (sortGroups[firstSortGroupIndex].ticketData?.positionInCategory ??
            0) -
          (sortGroups[secondSortGroupIndex].ticketData?.positionInCategory ?? 0)
      );

    if (movedSortGroupIndex !== undefined && targetPosition !== undefined) {
      const movedCategoryIndex: number =
        categorySortGroupIndexes.indexOf(movedSortGroupIndex);

      if (movedCategoryIndex >= 0) {
        categorySortGroupIndexes.splice(movedCategoryIndex, 1);
        categorySortGroupIndexes.splice(
          Math.max(
            0,
            Math.min(targetPosition, categorySortGroupIndexes.length)
          ),
          0,
          movedSortGroupIndex
        );
      }
    }

    const positionsBySortGroupIndex: Map<number, number> = new Map(
      categorySortGroupIndexes.map(
        (sortGroupIndex: number, index: number): [number, number] => [
          sortGroupIndex,
          index,
        ]
      )
    );

    return sortGroups.map(
      (
        sortGroup: ISortGroupWithTicketData,
        sortGroupIndex: number
      ): ISortGroupWithTicketData => {
        const position: number | undefined =
          positionsBySortGroupIndex.get(sortGroupIndex);

        if (position === undefined || !sortGroup.ticketData) {
          return sortGroup;
        }

        return {
          ...sortGroup,
          ticketData: {
            ...sortGroup.ticketData,
            positionInCategory: position,
          },
        };
      }
    );
  }
}
